import React, { useEffect, useState, useRef } from "react";
import { useLocation } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import { getCities } from "../../../services/CommonServices";
import {
  createAudience,
  updateAudienceAction,
} from "../../../store/actions/AudienceActions";
import { sendQRPassToGuestWhatsAppApi } from "../../../services/AudienceService";

import FormUI from "./FormUI";

const CustomForm = () => {
  const [audienceData, setAudienceData] = useState();
  const [selectedCity, setSelectedCity] = useState({});
  const [selectedCountry, setSelectedCountry] = useState("+91");
  const systemConfigs = useSelector((state) => state.systemconfig);
  const location = useLocation();
  const dispatch = useDispatch();
  const qrPassRef = useRef(null);
  const handleFormSubmit = async (values, { setSubmitting }) => {
    try {
      let response = null;
      if (audienceData?._id) {
        // Prepare update payload - explicitly include only the fields we want to update
        const updatePayload = {
          _id: audienceData?._id,
          firstname: values.firstname.trim(),
          lastname: values.lastname.trim(),
          gender: values.gender ? values.gender.toLowerCase() : values.gender,
        };

        // Only include email if it has been changed from the original value
        const originalEmail = (audienceData?.email || "").trim();
        const newEmail = (values.email || "").trim();
        if (originalEmail !== newEmail) {
          updatePayload.email = newEmail;
        }

        // Check if firstname, lastname, or guests have changed
        // If so, clear qr_pass_image_url to force regeneration on next WhatsApp send
        const firstnameChanged = (audienceData?.firstname || "").trim() !== values.firstname.trim();
        const lastnameChanged = (audienceData?.lastname || "").trim() !== values.lastname.trim();
        
        // Filter out empty guests and ensure country_code is included for update
        // Always process guests array to ensure it's included in the update, even if empty
        const guests = (values.guests || [])
          .filter(guest => {
            const firstName = String(guest?.firstname || "").trim();
            const mobile = String(guest?.mobile || "").trim();
            return firstName || mobile;
          })
          .map(guest => ({
            firstname: String(guest?.firstname || "").trim() || "",
            mobile: String(guest?.mobile || "").trim() || "",
            country_code: guest?.country_code || "+91",
          }));
        
        // Always include guests array in update payload (even if empty array to remove all guests)
        updatePayload.guests = guests;
        
        // Include other fields that might be in the form
        if (values.city) updatePayload.city = values.city;
        if (values.hear_about) updatePayload.hear_about = values.hear_about;
        if (values.audience_type) updatePayload.audience_type = values.audience_type;

        // Compare guests - check if count or any guest details changed
        const originalGuests = audienceData?.guests || [];
        const newGuests = guests;
        const guestsChanged = originalGuests.length !== newGuests.length || 
          originalGuests.some((guest, index) => {
            const newGuest = newGuests[index];
            const originalFirstName = String(guest?.firstname || "").trim();
            const newFirstName = String(newGuest?.firstname || "").trim();
            const originalMobile = String(guest?.mobile || "").trim();
            const newMobile = String(newGuest?.mobile || "").trim();
            return originalFirstName !== newFirstName || originalMobile !== newMobile;
          });

        // Check if new guests were added (guests with mobile numbers that weren't in original list)
        const originalMobiles = new Set(
          originalGuests
            .filter(guest => String(guest?.mobile || "").trim())
            .map(guest => String(guest.mobile || "").trim())
        );
        const newlyAddedGuests = guests.filter(guest => {
          const mobile = String(guest?.mobile || "").trim();
          return mobile && !originalMobiles.has(mobile);
        });

        // If any QR pass content changed, clear the image URL
        if (firstnameChanged || lastnameChanged || guestsChanged) {
          updatePayload.qr_pass_image_url = "";
        }

        // Debug: Log the payload being sent
        console.log("Update Audience Payload:", JSON.stringify(updatePayload, null, 2));
        console.log("Guests array being sent:", guests);
        console.log("Original guests count:", originalGuests.length);
        console.log("New guests count:", guests.length);

        response = await dispatch(
          updateAudienceAction(
            updatePayload,
            audienceData?._id
          )
        );

        // Check if update was successful
        if (!response?.data?.data) {
          toast.error("Failed to update audience. Please try again.");
          setSubmitting(false);
          return;
        }

        const updatedAudienceData = response.data.data;
        
        // Debug: Log the response to verify guests were removed
        console.log("Update Audience Response:", JSON.stringify(updatedAudienceData, null, 2));
        console.log("Guests in response:", updatedAudienceData?.guests);
        console.log("Guests count in response:", updatedAudienceData?.guests?.length || 0);
        
        setAudienceData(updatedAudienceData);
        toast.success("Audience updated successfully!");

        // Send QR pass to newly added guests only (must have at least 1 guest with mobile)
        if (newlyAddedGuests.length > 0) {
          // Wait for QR pass component to render, then generate image if needed
          let qrPassImageUrl = updatedAudienceData?.qr_pass_image_url;
          
          if (!qrPassImageUrl) {
            // Wait a bit for the component to render
            await new Promise(resolve => setTimeout(resolve, 500));
            
            // Try multiple times to ensure component is rendered
            let attempts = 0;
            const maxAttempts = 5;
            while (attempts < maxAttempts && !qrPassImageUrl) {
              if (qrPassRef?.current?.generateQRPassImage) {
                try {
                  qrPassImageUrl = await qrPassRef.current.generateQRPassImage();
                  
                  // The generateQRPassImage function already updates the audience via API
                  // Wait a moment for the update to complete, then verify
                  if (qrPassImageUrl) {
                    await new Promise(resolve => setTimeout(resolve, 300));
                    
                    // Update local state
                    setAudienceData(prev => ({
                      ...prev,
                      qr_pass_image_url: qrPassImageUrl
                    }));
                    break;
                  }
                } catch (error) {
                  console.error("Error generating QR pass image:", error);
                }
              }
              
              if (!qrPassImageUrl && attempts < maxAttempts - 1) {
                await new Promise(resolve => setTimeout(resolve, 300));
              }
              attempts++;
            }
          }

          // Only send if we have QR pass image URL AND at least 1 guest with mobile
          if (qrPassImageUrl) {
            const guestMobiles = newlyAddedGuests
              .filter(guest => String(guest?.mobile || "").trim())
              .map(guest => String(guest.mobile || "").trim());
            const guestCountryCodes = newlyAddedGuests
              .filter(guest => String(guest?.mobile || "").trim())
              .map(guest => guest?.country_code || "+91");

            // Ensure we have at least 1 guest with mobile number
            if (guestMobiles.length > 0 && guestMobiles.length === guestCountryCodes.length) {
              try {
                await sendQRPassToGuestWhatsAppApi({
                  qrImageUrl: qrPassImageUrl,
                  mobile: guestMobiles,
                  country_code: guestCountryCodes,
                });
                toast.success(`QR pass sent to ${guestMobiles.length} guest(s) via WhatsApp!`);
              } catch (error) {
                console.error("Failed to send QR pass to guests:", error);
                toast.error("Failed to send QR pass to guests. Please try again.");
              }
            }
          } else {
            toast.warning("Could not generate QR pass image. Please try sending manually.");
          }
        }
      } else {
        // Filter out empty guests and ensure country_code is included
        const guests = (values.guests || [])
          .filter(guest => {
            const firstName = String(guest?.firstname || "").trim();
            const mobile = String(guest?.mobile || "").trim();
            return firstName || mobile;
          })
          .map(guest => ({
            firstname: String(guest?.firstname || "").trim() || "",
            mobile: String(guest?.mobile || "").trim() || "",
            country_code: guest?.country_code || "+91",
          }));

        response = await dispatch(
          createAudience({
            ...values,
            firstname: values.firstname.trim(),
            lastname: values.lastname.trim(),
            gender: values.gender ? values.gender.toLowerCase() : values.gender,
            email: values.email ? values.email.trim() : values.email,
            guests: guests,
          })
        );

        // Check if creation was successful
        if (!response?.data?.data) {
          toast.error("Failed to create audience. Please try again.");
          setSubmitting(false);
          return;
        }

        const createdAudienceData = response.data.data;
        setAudienceData(createdAudienceData);
        toast.success("Audience created successfully!");

        // Send QR pass to guests if guests were added during creation (must have at least 1 guest with mobile)
        if (guests.length > 0) {
          // Wait for QR pass component to render, then generate image if needed
          let qrPassImageUrl = createdAudienceData?.qr_pass_image_url;
          
          if (!qrPassImageUrl) {
            // Wait a bit for the component to render
            await new Promise(resolve => setTimeout(resolve, 500));
            
            // Try multiple times to ensure component is rendered
            let attempts = 0;
            const maxAttempts = 5;
            while (attempts < maxAttempts && !qrPassImageUrl) {
              if (qrPassRef?.current?.generateQRPassImage) {
                try {
                  qrPassImageUrl = await qrPassRef.current.generateQRPassImage();
                  
                  // The generateQRPassImage function already updates the audience via API
                  // Wait a moment for the update to complete, then verify
                  if (qrPassImageUrl) {
                    await new Promise(resolve => setTimeout(resolve, 300));
                    
                    // Update local state
                    setAudienceData(prev => ({
                      ...prev,
                      qr_pass_image_url: qrPassImageUrl
                    }));
                    break;
                  }
                } catch (error) {
                  console.error("Error generating QR pass image:", error);
                }
              }
              
              if (!qrPassImageUrl && attempts < maxAttempts - 1) {
                await new Promise(resolve => setTimeout(resolve, 300));
              }
              attempts++;
            }
          }

          // Only send if we have QR pass image URL AND at least 1 guest with mobile
          if (qrPassImageUrl) {
            const guestMobiles = guests
              .filter(guest => String(guest?.mobile || "").trim())
              .map(guest => String(guest.mobile || "").trim());
            const guestCountryCodes = guests
              .filter(guest => String(guest?.mobile || "").trim())
              .map(guest => guest?.country_code || "+91");

            // Ensure we have at least 1 guest with mobile number and arrays match
            if (guestMobiles.length > 0 && guestMobiles.length === guestCountryCodes.length) {
              try {
                await sendQRPassToGuestWhatsAppApi({
                  qrImageUrl: qrPassImageUrl,
                  mobile: guestMobiles,
                  country_code: guestCountryCodes,
                });
                toast.success(`QR pass sent to ${guestMobiles.length} guest(s) via WhatsApp!`);
              } catch (error) {
                console.error("Failed to send QR pass to guests:", error);
                toast.error("Failed to send QR pass to guests. Please try again.");
              }
            }
          } else {
            toast.warning("Could not generate QR pass image. Please try sending manually.");
          }
        }
      }
      setSubmitting(false);
    } catch (error) {
      console.error("Error submitting form:", error);
      toast.error("An error occurred while saving. Please try again.");
      setSubmitting(false);
    }
  };
  useEffect(() => {
    dispatch(
      getCities({
        countryCode: selectedCountry?.toString()?.startsWith("+")
          ? selectedCountry?.toString().replace("+", "")
          : selectedCountry,
      })
    );
  }, [selectedCountry]);
  // Initialize guests array with default country_code
  const maxGuestCount = systemConfigs?.guest_count ?? 2;
  const initialGuests = Array.from({ length: maxGuestCount }).map(() => ({
    firstname: "",
    mobile: "",
    country_code: "+91",
  }));

  let initialValues = {
    firstname: "",
    lastname: "",
    mobile: "",
    gender: "",
    city: "",
    email: "",
    country_code: "+91",
    hear_about: "admin",
    audience_type: "audience",
    guests: initialGuests,
  };
  if (location?.state) {
    // If editing, merge existing guests with default structure
    const existingGuests = location?.state?.guests || [];
    const mergedGuests = Array.from({ length: maxGuestCount }).map((_, index) => ({
      firstname: existingGuests[index]?.firstname || "",
      mobile: existingGuests[index]?.mobile || "",
      country_code: existingGuests[index]?.country_code || "+91",
    }));

    initialValues = { 
      ...location?.state, 
      city: location?.state?.city?._id,
      // Normalize gender to lowercase for comparison
      gender: location?.state?.gender ? location.state.gender.toLowerCase() : "",
      guests: mergedGuests,
    };
  }
  useEffect(() => {
    if (location?.state) {
      setAudienceData(location?.state);
      setSelectedCity({
        label: location.state.city?.name ?? "",
        value: location.state.city?._id ?? "",
      });
    }
  }, []);

  return (
    <FormUI
      audienceData={audienceData}
      handleFormSubmit={handleFormSubmit}
      initialValues={initialValues}
      selectedCity={selectedCity}
      setSelectedCity={setSelectedCity}
      setSelectedCountry={setSelectedCountry}
      maxGuest={systemConfigs?.guest_count ?? 2}
      qrPassRef={qrPassRef}
    />
  );
};

export default CustomForm;
